/**
 * Ranked edit instructions list with effort indicators
 */

import { Card } from '@/components/ui/Card';
import { colors } from '@/lib/design-tokens';
import type { EditInstruction, EffortLevel } from '@/types';

export interface EditListCardProps {
  edits: EditInstruction[];
  onTimestampClick: (timestamp: string) => void;
}

function getEffortBadge(effort: EffortLevel) {
  const styles = {
    S: {
      bg: colors.status.successSoft,
      text: colors.status.success,
      label: 'S',
    },
    M: {
      bg: colors.status.warningSoft,
      text: colors.status.warning,
      label: 'M',
    },
    L: {
      bg: colors.status.dangerSoft,
      text: colors.status.danger,
      label: 'L',
    },
  };

  const style = styles[effort];

  return (
    <span
      className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold"
      style={{
        backgroundColor: style.bg,
        color: style.text,
      }}
      title={`Effort: ${effort === 'S' ? 'Small' : effort === 'M' ? 'Medium' : 'Large'}`}
    >
      {style.label}
    </span>
  );
}

export function EditListCard({ edits, onTimestampClick }: EditListCardProps) {
  // Sort edits by rank
  const sortedEdits = [...edits].sort((a, b) => a.rank - b.rank);

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-sm font-semibold text-gray-900">Recommended Edits</h3>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <span
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: colors.status.successSoft }}
            />
            Small
          </span>
          <span className="flex items-center gap-1">
            <span
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: colors.status.warningSoft }}
            />
            Medium
          </span>
          <span className="flex items-center gap-1">
            <span
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: colors.status.dangerSoft }}
            />
            Large
          </span>
        </div>
      </div>

      <div className="space-y-4">
        {sortedEdits.map((edit) => (
          <div
            key={edit.rank}
            className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
          >
            {/* Header with Rank, Timestamp, and Effort */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                {/* Rank Badge */}
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-900 text-white font-bold text-sm">
                  {edit.rank}
                </div>

                {/* Timestamp (Clickable) */}
                <button
                  onClick={() => onTimestampClick(edit.timestamp)}
                  className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-mono font-semibold transition-colors"
                  title="Seek to this timestamp"
                >
                  {edit.timestamp}
                </button>
              </div>

              {/* Effort Badge */}
              {getEffortBadge(edit.effort)}
            </div>

            {/* Instruction */}
            <div className="mb-3">
              <p className="text-sm text-gray-900 leading-relaxed">
                {edit.instruction}
              </p>
            </div>

            {/* Principle */}
            <div className="flex items-start gap-2 text-xs">
              <span className="text-gray-500 font-semibold uppercase tracking-wider">
                Principle:
              </span>
              <span className="text-gray-600">{edit.principle}</span>
            </div>
          </div>
        ))}
      </div>

      {sortedEdits.length === 0 && (
        <div className="py-12 text-center">
          <svg
            className="w-12 h-12 mx-auto text-gray-300 mb-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-sm text-gray-500">No edits recommended</p>
          <p className="text-xs text-gray-400 mt-1">
            This creative is ready to scale!
          </p>
        </div>
      )}
    </Card>
  );
}
